// Global packages and functions
import { useLocation } from '@remix-run/react'
import { useEffect } from 'react'
import {
  createGtmScript,
  createGtmNoScript,
  TrustpilotScript,
  RecaptchaScript,
} from '~/utils'

// Types
import type { NonceType } from '~/types'

interface CustomScriptsProps {
  disable?: boolean
  gtmId?: string
  nonce?: NonceType
  recaptchaSiteKey?: string
}

// Main export
const useCustomScripts = ({
  disable = false,
  gtmId,
  nonce,
  recaptchaSiteKey,
}: CustomScriptsProps = {}) => {
  const location = useLocation()

  /*
   * ADD EXTERNAL SCRIPTS TO SITE (avoid hydration errors via useEffect)
   */
  useEffect(() => {
    if (!disable) {
      /*
       * GTM
       */
      const gtmEl = document.getElementById('tracking-gtm')
      const gtmElInsert = document.getElementById('tracking-gtm-insert')

      // Check elements don't already exist and gtmId has been set
      if (!gtmEl && !gtmElInsert && gtmId) {
        const gtmScript = createGtmScript(gtmId, nonce)

        if (gtmScript) {
          document.head.appendChild(gtmScript)
        }
      }

      /*
       * GTM NO SCRIPT
       */
      const gtmElNoScript = document.getElementById('tracking-gtm-noscript')

      // Check elements don't already exist and gtmId has been set
      if (!gtmElNoScript && gtmId) {
        const gtmNoScript = createGtmNoScript(gtmId, nonce)

        if (gtmNoScript) {
          document.body.insertBefore(gtmNoScript, document.body.firstChild)
        }
      }

      /*
       * Trustpilot SCRIPT
       */
      const trustpilotElScript = document.getElementById('trustpilot-script')

      // Check elements don't already exist
      if (!trustpilotElScript) {
        const trustpilotScript = TrustpilotScript(nonce)

        if (trustpilotScript) {
          document.head.appendChild(trustpilotScript)
        }
      }

      /*
       * reCAPTCHA v3 SCRIPT — persistent security script. Added once and
       * deliberately NOT removed on unmount (unlike the tracking scripts),
       * so grecaptcha stays available across navigations.
       */
      const recaptchaElScript = document.getElementById('recaptcha-script')

      // Check element doesn't already exist and a site key has been set
      if (!recaptchaElScript && recaptchaSiteKey) {
        const recaptchaScript = RecaptchaScript(recaptchaSiteKey, nonce)

        if (recaptchaScript) {
          document.head.appendChild(recaptchaScript)
        }
      }

      /**
       * Remove elements on unmount
       */
      return () => {
        if (gtmEl) {
          gtmEl.remove()
        }

        if (gtmElInsert) {
          gtmElInsert.remove()
        }

        if (gtmElNoScript) {
          gtmElNoScript.remove()
        }

        if (trustpilotElScript) {
          trustpilotElScript.remove()
        }
      }
    }
  }, [location, gtmId, nonce, disable, recaptchaSiteKey])

  return null
}

export default useCustomScripts
